/**
 * AsyncTab — 비동기 처리 (C019) 라이브 데모
 * POST /api/eraf/platform-lab/async/submit  → taskId 즉시 반환
 * GET  /api/eraf/platform-lab/async/tasks/{taskId} → 1s polling
 * 5상태: default / loading(RUNNING badge) / empty / error / dialog(N/A — stateless)
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EmptyState, ErrorState } from '@ui-kit';
import { useSubmitAsyncTask, useAsyncTaskResult } from '../hooks/usePlatformLab';
import { asyncSubmitSchema } from '../schema/platformLab.schema';
import type { AsyncSubmitFormValues } from '../schema/platformLab.schema';
import type { AsyncSubmitResponse, TaskStatus, ApiError } from '../types/platformLab';

function statusColor(status: TaskStatus): string {
  if (status === 'DONE') return 'var(--color-success, #28a745)';
  if (status === 'FAILED') return 'var(--color-danger)';
  if (status === 'RUNNING') return 'var(--color-warning, #f0ad4e)';
  return 'var(--color-text-muted)';
}

function StatusBadge({ status, t }: { status: TaskStatus; t: (k: string) => string }) {
  const color = statusColor(status);
  const isRunning = status === 'RUNNING' || status === 'PENDING';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-1)',
        padding: 'var(--spacing-1) var(--spacing-2)',
        border: `1px solid ${color}`,
        borderRadius: 'var(--radius-sm)',
        color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 600,
        animation: isRunning ? 'statusPulse 1.2s ease-in-out infinite' : 'none',
      }}
    >
      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color }} />
      {t(`platformLab.async.status.${status}`)}
    </span>
  );
}

export function AsyncTab() {
  const { t } = useTranslation();
  const submitMutation = useSubmitAsyncTask();

  const [submitResult, setSubmitResult] = useState<AsyncSubmitResponse | null>(null);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);
  const [pollingTaskId, setPollingTaskId] = useState<string | null>(null);

  const taskQuery = useAsyncTaskResult(pollingTaskId);

  const { register, handleSubmit, formState: { errors } } = useForm<AsyncSubmitFormValues>({
    resolver: zodResolver(asyncSubmitSchema),
    defaultValues: { delayMs: 800, label: 'demo' },
  });

  const onSubmit = handleSubmit((values) => {
    setSubmitError(null);
    setSubmitResult(null);
    setPollingTaskId(null);
    submitMutation.mutate(values, {
      onSuccess: (data) => {
        setSubmitResult(data as AsyncSubmitResponse);
        setPollingTaskId((data as AsyncSubmitResponse).taskId);
      },
      onError: (err) => {
        setSubmitError(err as unknown as ApiError);
      },
    });
  });

  const taskResult = taskQuery.data;
  const isPolling = !!pollingTaskId && (taskResult?.status === 'PENDING' || taskResult?.status === 'RUNNING' || !taskResult);
  const isDone = taskResult?.status === 'DONE';
  const isFailed = taskResult?.status === 'FAILED';

  return (
    <div>
      <h2 style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, marginBottom: 'var(--spacing-4)', color: 'var(--color-text-primary)' }}>
        {t('platformLab.async.title')}
      </h2>

      {/* mainTraceId 표시 (submitResult 있을 때) */}
      {submitResult?.mainTraceId && (
        <div style={{ marginBottom: 'var(--spacing-3)', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)' }}>
          <span style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>{t('platformLab.async.label.mainTrace')}: </span>
          <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-primary)' }}>{submitResult.mainTraceId}</code>
        </div>
      )}

      {/* 제출 폼 */}
      <form
        onSubmit={(e) => e.preventDefault()}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', alignItems: 'flex-end', marginBottom: 'var(--spacing-5)' }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('platformLab.async.input.delay')}
          <input
            {...register('delayMs')}
            type="number"
            min={0}
            max={5000}
            aria-label={t('platformLab.async.input.delay')}
            aria-invalid={!!errors.delayMs}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${errors.delayMs ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              width: 100,
              fontFamily: 'var(--font-family-sans)',
            }}
          />
          {errors.delayMs && <span style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>{errors.delayMs.message}</span>}
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {t('platformLab.async.input.label')}
          <input
            {...register('label')}
            type="text"
            aria-label={t('platformLab.async.input.label')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              width: 120,
              fontFamily: 'var(--font-family-sans)',
            }}
          />
        </label>

        <button
          type="button"
          onClick={onSubmit}
          disabled={submitMutation.isPending}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: submitMutation.isPending ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            opacity: submitMutation.isPending ? 0.6 : 1,
          }}
        >
          {submitMutation.isPending ? '...' : t('platformLab.async.button.submit')}
        </button>
      </form>

      {/* loading — 제출 중 */}
      {submitMutation.isPending && (
        <div aria-busy="true" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--color-brand-primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
          작업 제출 중...
        </div>
      )}

      {/* error — 제출 실패 */}
      {submitError && !submitMutation.isPending && (
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <ErrorState
            title={t(`platformLab.error.${submitError.code}`, { defaultValue: t('platformLab.error.network') })}
            message={submitError.message}
            onRetry={() => setSubmitError(null)}
          />
        </div>
      )}

      {/* empty — 아직 제출 전 */}
      {!submitResult && !submitError && !submitMutation.isPending && (
        <EmptyState title={t('platformLab.async.empty.title')} />
      )}

      {/* 작업 상태 카드 (제출 후) */}
      {submitResult && !submitError && !submitMutation.isPending && (
        <div
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-background)',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-3)', alignItems: 'center' }}>
            <div>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>{t('platformLab.async.label.taskId')}: </span>
              <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}>{submitResult.taskId}</code>
            </div>
            {taskResult && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>{t('platformLab.async.label.status')}: </span>
                <StatusBadge status={taskResult.status} t={t} />
              </div>
            )}
            {/* loading indicator — polling 중 */}
            {isPolling && (
              <span
                aria-label="polling"
                aria-busy="true"
                style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--color-brand-primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }}
              />
            )}
          </div>

          {/* DONE — traceId 전파 확인 */}
          {isDone && taskResult && (
            <div
              style={{
                padding: 'var(--spacing-3)',
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <div style={{ marginBottom: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)' }}>
                <span style={{ fontWeight: 600, color: 'var(--color-text-secondary)' }}>{t('platformLab.async.label.workerThread')}: </span>
                <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-primary)' }}>{taskResult.workerThread ?? '-'}</code>
              </div>
              <div style={{ marginBottom: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)' }}>
                <span style={{ fontWeight: 600, color: 'var(--color-text-secondary)' }}>{t('platformLab.async.label.workerTrace')}: </span>
                <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-primary)' }}>{taskResult.workerTraceId ?? '-'}</code>
              </div>
              <div
                style={{
                  marginTop: 'var(--spacing-3)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  borderRadius: 'var(--radius-sm)',
                  background: taskResult.traceIdMatched
                    ? 'var(--color-success-subtle, #e6f9f0)'
                    : 'var(--color-danger-subtle, #fce8e8)',
                  border: `1px solid ${taskResult.traceIdMatched ? 'var(--color-success, #28a745)' : 'var(--color-danger)'}`,
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: taskResult.traceIdMatched ? 'var(--color-success, #28a745)' : 'var(--color-danger)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                }}
              >
                {taskResult.traceIdMatched ? (
                  <>
                    <span>✓</span>
                    <span>{t('platformLab.async.matched')}</span>
                  </>
                ) : (
                  <>
                    <span>✗</span>
                    <span>{t('platformLab.async.notMatched')}</span>
                  </>
                )}
              </div>
              {taskResult.completedAt && (
                <div style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  completedAt: {taskResult.completedAt}
                </div>
              )}
            </div>
          )}

          {/* FAILED */}
          {isFailed && taskResult && (
            <ErrorState
              title={t('platformLab.error.network')}
              message="작업 실행에 실패했습니다"
              onRetry={() => {
                setSubmitResult(null);
                setPollingTaskId(null);
              }}
            />
          )}
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes statusPulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
      `}</style>
    </div>
  );
}
